import { useState, useEffect, useRef } from "react";
import img1 from "./Images/sm1.png";
import img2 from "./Images/sm2.png";
import img3 from "./Images/sm3.png";
import Navbar from "../HomePage/Navbar";
const NAV_LINKS = ["Overview", "Highlights", "Amenities", "Floor Plans", "Gallery", "Contact"];

const CONFIGS = [
  {
    type: "2 BHK",
    area: "1,280 – 1,360 sq.ft.",
    icon: "🏠",
    tag: "Starter",
    desc: "Thoughtfully designed for young families and professionals seeking refined urban living.",
  },
  {
    type: "3 BHK SMART",
    area: "1,490 – 1,620 sq.ft.",
    icon: "🏡",
    tag: "Popular",
    desc: "Smart spaces that adapt to your lifestyle with intelligent room planning.",
  },
  {
    type: "3 BHK CLASSIC",
    area: "1,650 – 1,780 sq.ft.",
    icon: "🏰",
    tag: "Best Value",
    desc: "Classic proportions with premium finishes — timeless and elegant.",
  },
  {
    type: "3 BHK GRAND",
    area: "1,800 – 1,920 sq.ft.",
    icon: "👑",
    tag: "Grand",
    desc: "Expansive layouts for those who appreciate grandeur in everyday living.",
  },
  {
    type: "4 BHK SMART",
    area: "1,960 – 2,100 sq.ft.",
    icon: "🌟",
    tag: "Premium",
    desc: "Four bedrooms designed for growing families with space to breathe and flourish.",
  },
  {
    type: "4 BHK GRAND",
    area: "2,150 – 2,295 sq.ft.",
    icon: "💎",
    tag: "Luxury",
    desc: "The pinnacle of Edition living — sprawling, sophisticated, and spectacular.",
  },
];

const AMENITIES = [
  { icon: "🏋️", label: "World-Class Gym" },
  { icon: "🏊", label: "Olympic Pool" },
  { icon: "🎾", label: "Tennis Courts" },
  { icon: "🧘", label: "Yoga Pavilion" },
  { icon: "🌿", label: "Central Green Spine" },
  { icon: "💧", label: "Water Features" },
  { icon: "🎭", label: "Amphitheatre" },
  { icon: "📚", label: "Library Lounge" },
  { icon: "🎮", label: "Gaming Zone" },
  { icon: "🍽️", label: "Fine Dining" },
  { icon: "🧒", label: "Kids' Play Area" },
  { icon: "🚴", label: "Cycling Track" },
  { icon: "🏓", label: "Squash Court" },
  { icon: "🌳", label: "Botanical Walk" },
  { icon: "🛁", label: "Spa & Wellness" },
  { icon: "🚗", label: "Valet Parking" },
];

const GALLERY_IMGS = [
   img1, img2, img3
];

const GALLERY_LABELS = ["Grand Exterior", "Living Spaces", "Luxury Interiors", "Community Life"];

function useIntersect(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, visible] = useIntersect();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function SumadhuraEdition() {
  const [activeNav, setActiveNav] = useState("Overview");
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [navScrolled, setNavScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setGalleryIdx((i) => (i + 1) % GALLERY_IMGS.length), 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans" style={{ fontFamily: "'Georgia', serif" }}>
      {/* NAV */}
     <Navbar/>

      {/* HERO */}
      <div className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden -mt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${GALLERY_IMGS[galleryIdx]})`, transition: "background-image 1s ease" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />

        {/* Decorative border */}
        <div className="absolute inset-6 border border-white/20 pointer-events-none hidden md:block" />
        <div className="absolute inset-8 border border-red-600/30 pointer-events-none hidden md:block" />

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <div className="inline-block border border-red-400/60 text-red-300 text-xs tracking-[0.4em] px-6 py-2 mb-6 uppercase">
            Siddapura · Whitefield · Bengaluru
          </div>
          <h1
            className="text-5xl sm:text-6xl md:text-8xl font-bold text-white leading-none mb-2"
            style={{ letterSpacing: "-0.02em", fontFamily: "'Georgia', serif" }}
          >
            SUMADHURA
          </h1>
          <h2
            className="text-3xl sm:text-4xl md:text-6xl font-light text-red-400 tracking-[0.2em] mb-6"
            style={{ fontFamily: "'Georgia', serif" }}
          >
            EDITION
          </h2>
          <p className="text-white/70 text-base md:text-lg tracking-widest max-w-xl mx-auto mb-10 font-light">
            Refined Living · British Inspired · Timeless Sophistication
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#brochure"
              className="px-8 py-3.5 bg-red-700 hover:bg-red-600 text-white font-bold tracking-widest text-sm transition-all rounded shadow-2xl shadow-red-900/50"
            >
              DOWNLOAD BROCHURE
            </a>
            <a
              href="#overview"
              className="px-8 py-3.5 border border-white/40 hover:border-white text-white font-semibold tracking-widest text-sm transition-all rounded"
            >
              EXPLORE PROJECT
            </a>
          </div>
        </div>

        {/* Gallery dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
          {GALLERY_IMGS.map((_, i) => (
            <button
              key={i}
              onClick={() => setGalleryIdx(i)}
              className={`w-2 h-2 rounded-full transition-all ${i === galleryIdx ? "bg-red-500 w-6" : "bg-white/40"}`}
            />
          ))}
        </div>
      </div>

      {/* QUICK STATS BAR */}
      <div className="bg-red-700 py-4 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "LOCATION", value: "Whitefield" },
            { label: "SPREAD", value: "~20 Acres" },
            { label: "HOMES", value: "~1,650+" },
            { label: "CLUBHOUSE", value: "68,000 sq.ft." },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-white font-bold text-xl md:text-2xl">{s.value}</div>
              <div className="text-red-200 text-xs tracking-widest mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* OVERVIEW */}
      <section id="overview" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <div className="text-red-600 text-xs tracking-[0.4em] mb-3 uppercase">About The Project</div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Georgia', serif" }}>
                Where British Elegance <br className="hidden md:block" />
                <span className="text-red-700">Meets Modern Living</span>
              </h2>
              <div className="w-16 h-0.5 bg-red-600 mx-auto mt-4 mb-6" />
              <p className="text-gray-500 max-w-2xl mx-auto leading-relaxed text-lg">
                Sumadhura Edition is a landmark development set across 20 pristine acres in the heart of Whitefield —
                Bengaluru's most coveted growth corridor. A curated community of 1,650 premium homes that embody
                timeless British architectural charm fused with contemporary sophistication.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: "🏰",
                title: "British-Inspired Architecture",
                desc: "Grand double-height lobbies, Georgian facades, and meticulous detailing inspired by England's finest residential communities.",
              },
              {
                icon: "🌿",
                title: "Central Green Spine",
                desc: "A lush green corridor winds through the community, connecting water features, gardens, and lifestyle zones seamlessly.",
              },
              {
                icon: "🏆",
                title: "Legacy Developer",
                desc: "Built by Sumadhura — Bengaluru's most trusted real estate brand with decades of landmark communities and impeccable delivery.",
              },
            ].map((c, i) => (
              <FadeIn key={c.title} delay={i * 0.1}>
                <div className="group p-8 border border-gray-100 hover:border-red-200 rounded-xl hover:shadow-xl transition-all bg-white">
                  <div className="text-4xl mb-5">{c.icon}</div>
                  <h3 className="font-bold text-gray-900 text-lg mb-3 group-hover:text-red-700 transition-colors" style={{ fontFamily: "'Georgia', serif" }}>
                    {c.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{c.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY ROW */}
      <section className="py-4 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          {GALLERY_IMGS.map((img, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div className="relative overflow-hidden rounded-lg aspect-video md:aspect-square group cursor-pointer">
                <img
                  src={img}
                  alt={GALLERY_LABELS[i]}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 text-white text-xs font-semibold tracking-widest">
                  {GALLERY_LABELS[i].toUpperCase()}
                </div>
                <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-red-700/80 flex items-center justify-center text-white text-xs">
                  {i + 1}
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* FLOOR PLANS */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <div className="text-red-600 text-xs tracking-[0.4em] mb-3 uppercase">Configurations</div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900" style={{ fontFamily: "'Georgia', serif" }}>
                Find Your <span className="text-red-700">Perfect Home</span>
              </h2>
              <div className="w-16 h-0.5 bg-red-600 mx-auto mt-4" />
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CONFIGS.map((c, i) => (
              <FadeIn key={c.type} delay={i * 0.07}>
                <div className="group relative border border-gray-100 hover:border-red-300 rounded-2xl p-6 hover:shadow-2xl transition-all bg-white overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-red-700 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-3xl">{c.icon}</span>
                    <span className="text-xs font-bold tracking-widest text-red-600 bg-red-50 border border-red-200 px-3 py-1 rounded-full">
                      {c.tag}
                    </span>
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1" style={{ fontFamily: "'Georgia', serif" }}>
                    {c.type}
                  </h3>
                  <div className="text-red-600 font-semibold text-sm mb-3 tracking-wide">{c.area}</div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{c.desc}</p>
                  <button className="text-xs font-bold tracking-widest text-red-700 group-hover:text-red-800 flex items-center gap-1.5">
                    VIEW FLOOR PLAN <span className="text-base">→</span>
                  </button>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS BANNER */}
      <section className="py-16 px-4 bg-red-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 border border-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 border border-white rounded-full translate-x-1/3 translate-y-1/3" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="text-red-200 text-xs tracking-[0.4em] mb-3 uppercase">Why Choose Edition</div>
              <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: "'Georgia', serif" }}>
                Crafted For The Discerning Few
              </h2>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: "🚇", title: "Metro Connected", desc: "Prime Whitefield address with direct metro and highway access" },
              { icon: "🧿", title: "Vaastu Compliant", desc: "All layouts designed in accordance with Vaastu Shastra principles" },
              { icon: "📈", title: "Strong ROI", desc: "High investment potential in Bengaluru's fastest-growing corridor" },
              { icon: "🏅", title: "Trusted Brand", desc: "Legacy developer with a proven record of landmark deliveries" },
            ].map((h, i) => (
              <FadeIn key={h.title} delay={i * 0.1}>
                <div className="text-center p-5">
                  <div className="text-4xl mb-3">{h.icon}</div>
                  <h3 className="font-bold text-white text-base mb-2">{h.title}</h3>
                  <p className="text-red-100 text-sm leading-relaxed">{h.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* AMENITIES */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <div className="text-red-600 text-xs tracking-[0.4em] mb-3 uppercase">Lifestyle Amenities</div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900" style={{ fontFamily: "'Georgia', serif" }}>
                150+ Curated <span className="text-red-700">Amenities</span>
              </h2>
              <div className="w-16 h-0.5 bg-red-600 mx-auto mt-4 mb-4" />
              <p className="text-gray-500 max-w-xl mx-auto">
                A 68,000 sq.ft. branded clubhouse anchors a world of fitness, leisure, and social experiences.
              </p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {AMENITIES.map((a, i) => (
              <FadeIn key={a.label} delay={i * 0.04}>
                <div className="group bg-white hover:bg-red-700 border border-gray-100 hover:border-red-700 rounded-xl p-4 text-center transition-all duration-300 cursor-default">
                  <div className="text-2xl mb-2">{a.icon}</div>
                  <div className="text-gray-600 group-hover:text-white text-xs font-semibold tracking-wide leading-tight transition-colors">
                    {a.label}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <FadeIn>
            <div>
              <div className="text-red-600 text-xs tracking-[0.4em] mb-3 uppercase">Prime Location</div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Georgia', serif" }}>
                Siddapura,<br /><span className="text-red-700">Whitefield</span>
              </h2>
              <p className="text-gray-500 leading-relaxed mb-8">
                Situated in the heart of Whitefield — Bengaluru's premier IT and lifestyle district — Sumadhura Edition
                places you at the epicentre of connectivity, culture, and commerce.
              </p>
              <div className="space-y-3">
                {[
                  "5 min to Whitefield Metro Station",
                  "10 min to ITPL Tech Park",
                  "15 min to Phoenix Marketcity",
                  "20 min to Kempegowda International Airport (via ORR)",
                  "Proximity to top schools, hospitals & retail",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-red-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-gray-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="bg-gray-100 rounded-2xl overflow-hidden h-80 md:h-96 flex items-center justify-center relative">
              <iframe
                title="Sumadhura Edition Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.4!2d77.7540!3d12.9767!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1329f0478a11%3A0x8aab7a5d6dda4e1!2sWhitefield%2C%20Bengaluru!5e0!3m2!1sen!2sin!4v1680000000000!5m2!1sen!2sin"
                className="w-full h-full border-0"
                allowFullScreen
                loading="lazy"
              />
              <div className="absolute top-4 left-4 bg-red-700 text-white text-xs font-bold tracking-widest px-3 py-1.5 rounded shadow-lg">
                📍 WHITEFIELD, BENGALURU
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* BROCHURE CTA */}
      <section id="brochure" className="py-24 px-4 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={GALLERY_IMGS[1]} alt="" className="w-full h-full object-cover opacity-10" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <FadeIn>
            <div className="border border-white/10 rounded-3xl p-10 md:p-14 backdrop-blur-sm">
              <div className="text-red-400 text-xs tracking-[0.4em] mb-4 uppercase">Your Dream Home Awaits</div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Georgia', serif" }}>
                Download the <span className="text-red-400">Brochure</span>
              </h2>
              <p className="text-white/60 mb-10 text-base leading-relaxed">
                Get comprehensive floor plans, amenity details, pricing insights, and everything you need to make your
                decision — all in one beautifully crafted document.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="#"
                  className="px-8 py-4 bg-red-700 hover:bg-red-600 text-white font-bold tracking-widest text-sm transition-all rounded-lg shadow-2xl shadow-red-900/50 flex items-center gap-2"
                  onClick={(e) => {
                    e.preventDefault();
                    alert("📄 Brochure download link would be here — connect your CRM or PDF asset.");
                  }}
                >
                  <span>📥</span> DOWNLOAD BROCHURE
                </a>
                <a
                  href="https://wa.me/message/MDG2ZSZNG6P6G1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 border border-white/30 hover:border-red-400 text-white hover:text-red-400 font-semibold tracking-widest text-sm transition-all rounded-lg flex items-center gap-2"
                >
                  <span>📋</span> SCHEDULE A VISIT / CONTACT
                </a>
              </div>
              <p className="text-white/30 text-xs mt-6 tracking-wide">
                RERA Registered · Sumadhura Infracon Pvt. Ltd.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-red-800 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <div className="font-bold text-white tracking-widest text-sm">SUMADHURA EDITION</div>
            <div className="text-red-200 text-xs tracking-widest mt-0.5">Siddapura · Whitefield · Bengaluru</div>
          </div>
          <div className="text-red-200 text-xs tracking-wide text-center">
            © 2025 Sumadhura Infracon Pvt. Ltd. All rights reserved. · RERA Registered
          </div>
          <div className="flex gap-4 text-red-200 text-xs">
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
            <a href="#" className="hover:text-white transition-colors">Terms</a>
            <a href="https://wa.me/message/MDG2ZSZNG6P6G1" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}